<script setup>
import {ElSwitch} from "element-plus";
import {useForm, usePage} from "@inertiajs/inertia-vue3";
import {ref, computed} from "vue";
import {TrashIcon, DocumentIcon} from "@heroicons/vue/outline";

const props = defineProps({
    agency_info: {type: Object, required: true},
});

const isSuperAdmin = computed(() => !!usePage().props.value.auth?.is_super_admin);

const formData = useForm({
    commissions_enabled: props.agency_info.commissions_enabled,
    uses_builtin_commissions_calendar: !!props.agency_info.uses_builtin_commissions_calendar,
    commissions_calendar_link_text: props.agency_info.commissions_calendar_link_text,
    commissions_calendar_url: props.agency_info.commissions_calendar_url,
    commissions_calendar_url_link_text: props.agency_info.commissions_calendar_url_link_text,
});

const usesBuiltIn = computed(() => formData.uses_builtin_commissions_calendar);

const loading = ref(false);

const submitForm = function(){
  if (loading.value) return;
  loading.value = true;
  formData.post(route('agencies.commissions-toggle', props.agency_info.id), {
    preserveScroll: true,
    preserveState: true,
    only: ['agency_info', 'errors'],
    onSuccess: () => {
      $notify.success({
        message: 'Saved',
        showClose: true,
      });
    },
    onError: () => {
    },
    onFinish: () => {
      loading.value = false;
    }
  });
};

// PDF Upload functionality (for non-agency-1 agencies)
const currentPdfUrl = ref(props.agency_info.commissions_calendar_pdf_url);
const pdfUploading = ref(false);
const pdfFileInput = ref(null);

const uploadPdf = async function(event) {
  const file = event.target.files[0];
  if (!file) return;

  if (file.type !== 'application/pdf') {
    $notify.error({
      message: 'Please select a PDF file',
      showClose: true,
    });
    return;
  }

  pdfUploading.value = true;

  const formDataUpload = new FormData();
  formDataUpload.append('file', file);

  try {
    const response = await axios.post(
      route('agencies.commissions-calendar-pdf.upload', props.agency_info.id),
      formDataUpload,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    currentPdfUrl.value = response.data.url;
    $notify.success({
      message: 'PDF uploaded successfully',
      showClose: true,
    });
  } catch (error) {
    $notify.error({
      message: error.response?.data?.message || 'Failed to upload PDF',
      showClose: true,
    });
  } finally {
    pdfUploading.value = false;
    if (pdfFileInput.value) {
      pdfFileInput.value.value = '';
    }
  }
};

const deletePdf = async function() {
  if (!confirm('Are you sure you want to delete this PDF?')) return;

  pdfUploading.value = true;

  try {
    await axios.delete(route('agencies.commissions-calendar-pdf.delete', props.agency_info.id));
    currentPdfUrl.value = null;
    $notify.success({
      message: 'PDF deleted successfully',
      showClose: true,
    });
  } catch (error) {
    $notify.error({
      message: error.response?.data?.message || 'Failed to delete PDF',
      showClose: true,
    });
  } finally {
    pdfUploading.value = false;
  }
};
</script>

<template>
<div class="p-4">
  <form method="POST" @submit.prevent="submitForm">
    <div class="flex space-x-4 items-center my-2">
      <el-switch v-model="formData.commissions_enabled" id="commissions_enabled" />
      <label class="cursor-pointer text-md" for="commissions_enabled">TopBroker Commission Features Enabled</label>
    </div>

    <!-- Super-admin-only: opt this agency into the built-in popup calendar -->
    <div v-if="isSuperAdmin" class="flex space-x-4 items-center my-2">
      <el-switch v-model="formData.uses_builtin_commissions_calendar" id="uses_builtin_commissions_calendar" />
      <label class="cursor-pointer text-md" for="uses_builtin_commissions_calendar">
        Use built-in Commissions Calendar (popup)
      </label>
    </div>
    <p v-if="isSuperAdmin" class="text-sm text-gray-500 -mt-1 mb-3">
      When on, agents see the TopBroker-managed HTML popup. When off, configure a PDF or external URL below.
    </p>

    <!-- Link Text (built-in: popup button label; otherwise: PDF button label) -->
    <div class="mt-6">
      <label class="block text-sm font-medium text-gray-700 mb-1" for="commissions_calendar_link_text">
        {{ usesBuiltIn ? 'Commissions Calendar Link Text' : 'PDF Link Text' }}
      </label>
      <input
        v-model="formData.commissions_calendar_link_text"
        type="text"
        id="commissions_calendar_link_text"
        class="input-field w-full max-w-md"
        placeholder="Commissions Calendar"
      />
      <p class="mt-1 text-sm text-gray-500">Leave empty to use the default: "Commissions Calendar"</p>
    </div>

    <!-- Non-Agency-1: URL and URL Link Text -->
    <div v-if="!usesBuiltIn" class="mt-6">
      <label class="block text-sm font-medium text-gray-700 mb-1" for="commissions_calendar_url">External URL</label>
      <input
        v-model="formData.commissions_calendar_url"
        type="url"
        id="commissions_calendar_url"
        class="input-field w-full max-w-md"
        placeholder="https://example.com/calendar"
      />
      <p class="mt-1 text-sm text-gray-500">External link to open when clicking the URL button</p>
    </div>

    <div v-if="!usesBuiltIn" class="mt-6">
      <label class="block text-sm font-medium text-gray-700 mb-1" for="commissions_calendar_url_link_text">URL Link Text</label>
      <input
        v-model="formData.commissions_calendar_url_link_text"
        type="text"
        id="commissions_calendar_url_link_text"
        class="input-field w-full max-w-md"
        placeholder="View Calendar"
      />
      <p class="mt-1 text-sm text-gray-500">Label for the URL button. Leave empty to use the default: "View Calendar"</p>
    </div>

    <div class="mt-4">
      <button class="btn btn-primary" :class="{'opacity-30': loading}" :disabled="loading" type="submit">Submit</button>
    </div>
  </form>

  <!-- PDF Upload Section (only for non-agency-1 agencies) -->
  <div v-if="!usesBuiltIn" class="mt-8 pt-6 border-t border-gray-200">
    <h3 class="text-lg font-medium text-gray-900 mb-4">Commissions Calendar PDF</h3>
    <p class="text-sm text-gray-500 mb-4">Upload a PDF that agents can view by clicking the PDF link.</p>

    <!-- Current PDF display -->
    <div v-if="currentPdfUrl" class="mb-4 p-4 bg-gray-50 rounded-lg flex items-center justify-between">
      <div class="flex items-center gap-3">
        <DocumentIcon class="w-8 h-8 text-red-500" />
        <div>
          <p class="text-sm font-medium text-gray-900">PDF Uploaded</p>
          <a :href="currentPdfUrl" target="_blank" class="text-sm text-blue-600 hover:underline">View PDF</a>
        </div>
      </div>
      <button
        type="button"
        @click="deletePdf"
        :disabled="pdfUploading"
        class="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
        title="Delete PDF"
      >
        <TrashIcon class="w-5 h-5" />
      </button>
    </div>

    <!-- Upload input -->
    <div class="flex items-center gap-4">
      <input
        ref="pdfFileInput"
        type="file"
        accept="application/pdf"
        @change="uploadPdf"
        :disabled="pdfUploading"
        class="block w-full max-w-md text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 disabled:opacity-50"
      />
      <span v-if="pdfUploading" class="text-sm text-gray-500">Uploading...</span>
    </div>
  </div>

  <!-- Notice for Agency 1 -->
  <div v-else class="mt-8 pt-6 border-t border-gray-200">
    <p class="text-sm text-gray-500">This agency uses the built-in Commissions Calendar.</p>
  </div>
</div>
</template>